import React, { useState, useEffect, useCallback, useMemo } from 'react';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';

const toFa = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = s => String(s).replace(/[۰-۹]/g,d=>'۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const numFmt = n => { const v=Math.abs(n||0); return v ? v.toLocaleString('fa-IR') : '•'; };
const si = v => { try{return parseInt(toEng(String(v||0)),10)||0;}catch{return 0;} };

/* فراخوانی API + رفرش توکن */
async function API(path, opts={}){
  const doFetch=()=>fetch(`/api/accounting${path}`,{
    headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts});
  let res=await doFetch();
  if(res.status===401){
    const rr=await fetch('/api/auth/token/refresh/',{method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({refresh:localStorage.getItem('refresh')})});
    if(rr.ok){const d=await rr.json();localStorage.setItem('access',d.access);res=await doFetch();}
  }
  return res.json();
}

export default function ReceiveReadyModal({ year, onClose, onAdded }) {
  const [rawRows,    setRawRows]    = useState([]);
  const [checked,    setChecked]    = useState({});
  const [readyNo,    setReadyNo]    = useState('');
  const [docDate,    setDocDate]    = useState('');
  const [serverTotal, setServerTotal] = useState(0);
  const [serverHasMore, setServerHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [toast,      setToast]      = useState('');
  const [yearFilter, setYearFilter] = useState('');

  // عرض ستون‌ها
  const { widths, TH } = useColumnResize([35,50,45,55,70,70,130,110,110,100,75,85,85,85,85,55,55,75,55]);

  const yearFiltered = useMemo(() => {
    const yf = toEng(yearFilter).trim();
    if (!yf) return rawRows;
    return rawRows.filter(r => String(r.financial_year).startsWith(yf));
  }, [rawRows, yearFilter]);

  const {
    visibleRows, search, setSearch,
    filteredCount, shownCount, LoadMoreButton, resetPagination,
  } = useTablePagination({
    allRows: yearFiltered, pageSize: 200,
    searchFields: ['credit_display','program_display','project_display','hazineh_display','remark','letter_no'],
      serverTotal,
    serverHasMore,
  });

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),4000); };

  useEffect(() => {
    API(`/documents/next-no/?year=${year}`).then(j => { if (j.ok) setDocDate(j.last_date||''); });
  }, [year]);

  /* خواندن فهرست از سرور */
  const load = useCallback(async () => {
    setLoading(true); setError(''); setRawRows([]); setChecked({}); resetPagination();
    const j = await API(`/auto-document/receive/ready/?year=${year}`);
    setLoading(false);
    if (j.ok) { setRawRows(j.data); setServerTotal(j.total||j.count||0); setServerHasMore(j.has_more||false); setCurrentPage(1); } else setError(j.msg);
  }, [year, resetPagination]);

  /* ۲۰۰ ردیف بعدی */
  const loadMore = async () => {
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    const j = await API(`/auto-document/receive/ready/?year=${year}&page=${nextPage}`);
    setLoadingMore(false);
    if (j.ok) {
      setRawRows(prev => [...prev, ...j.data]);
      setServerTotal(j.total||0);
      setServerHasMore(j.has_more||false);
      setCurrentPage(nextPage);
    }
  };

  useEffect(() => { load(); }, [load]);

  const rowKey = r => r.budget_transaction_id;
  const toggleAll = val => {
    const next = { ...checked };
    visibleRows.forEach(r => { if (val) next[rowKey(r)] = true; else delete next[rowKey(r)]; });
    setChecked(val ? { ...checked, ...next } : (() => { visibleRows.forEach(r => delete next[rowKey(r)]); return next; })());
  };
  const toggleOne = (k, val) => setChecked(c => { const n={...c}; if(val) n[k]=true; else delete n[k]; return n; });

  const selectedRows = rawRows.filter(r => checked[rowKey(r)]);
  const totalCash = selectedRows.reduce((s,r) => s + Math.abs(r.receive_cash_pay||0), 0);
  const totalFund = selectedRows.reduce((s,r) => s + Math.abs(r.receive_fund_pay||0), 0);
  const totalBack = selectedRows.reduce((s,r) => s + Math.abs(r.back_receive_fund_pay||0), 0);
  const totalDoc  = selectedRows.reduce((s,r) => s + Math.abs(r.receive_document_pay||0), 0);
  const isNewDoc = si(readyNo) === 0;
  const allVisibleChecked = visibleRows.length>0 && visibleRows.every(r => checked[rowKey(r)]);

  /* افزودن به سند */
  const addToDoc = async () => {
    if (!selectedRows.length) { setError('هیچ ردیفی انتخاب نشده'); return; }
    if (isNewDoc && !docDate) { setError('تاریخ سند جدید الزامیست'); return; }
    setSaving(true); setError('');
    const j = await API('/auto-document/receive/add/', {
      method:'POST',
      body: JSON.stringify({
        financial_year:year, ready_document_no:si(readyNo), doc_date:docDate,
        rows: rawRows.map(r => ({ ...r, selected:!!checked[rowKey(r)] })),
      }),
    });
    setSaving(false);
    if (j.ok) { showToast(j.msg); onAdded&&onAdded(); load(); } else setError(j.msg);
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e=>e.stopPropagation()}>
        <div style={s.hdr}>
          <div>
            <div style={{fontSize:'15px',fontWeight:'700',color:'#fff'}}>📥 دریافتی — دریافتی از خزانه</div>
            <div style={{fontSize:'11px',color:'rgba(255,255,255,0.6)',marginTop:'2px'}}>
              حساب‌های ۹۶/۹۳/۱۱/۲۳/۴۱ — سال مالی {toFa(year)}
            </div>
          </div>
          <button onClick={onClose} style={s.xBtn}>✕</button>
        </div>
        <div style={s.body}>
          {error && <div style={s.err}>⚠️ {error}</div>}

          <div style={{...s.card, background:'#fffdf5', border:'1.5px solid #f0e6c0'}}>
            <div style={{fontSize:'12px',fontWeight:'700',color:'#8d6e00',marginBottom:'8px'}}>🧾 سند مقصد</div>
            <div style={{display:'flex',gap:'14px',flexWrap:'wrap',alignItems:'flex-end'}}>
              <div style={s.fld}>
                <label style={s.lbl}>شماره سند مقصد</label>
                <input value={toFa(readyNo)}
                  onChange={e=>setReadyNo(toEng(e.target.value).replace(/\D/g,'').slice(0,6))}
                  placeholder="خالی = سند جدید" style={{...s.inp,width:'150px'}} />
              </div>
              {isNewDoc && (
                <div style={s.fld}>
                  <label style={s.lbl}>تاریخ سند جدید *</label>
                  <JalaliDatePicker value={docDate} onChange={v=>setDocDate(v)} placeholder="تاریخ"/>
                </div>
              )}
              <div style={{fontSize:'11px',color:'#8d6e00',flex:1,minWidth:'200px',lineHeight:'1.7'}}>
                {isNewDoc ? '↩ سند جدید ساخته می‌شود' : `↩ اقلام به سند ${toFa(readyNo)} اضافه می‌شود`}
              </div>
            </div>
          </div>

          <div style={{display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap'}}>
            <div style={{...s.fld,flex:1,minWidth:'200px'}}>
              <label style={s.lbl}>جستجو (منبع اعتبار / برنامه / پروژه / شرح)</label>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="جستجو..." style={{...s.inp,width:'100%'}} />
            </div>
            <div style={s.fld}>
              <label style={s.lbl}>فیلتر سال مالی</label>
              <input value={toFa(yearFilter)}
                onChange={e=>{ setYearFilter(toEng(e.target.value).replace(/\D/g,'').slice(0,4)); resetPagination(); }}
                placeholder="همه" style={{...s.inp,width:'90px'}} />
            </div>
            <button onClick={load} disabled={loading} style={s.btnS}>
              {loading?'⏳...':'↺ به‌روزرسانی'}
            </button>
            <span style={{fontSize:'11px',color:'#888'}}>{toFa(shownCount)} از {toFa(filteredCount)} ردیف</span>
          </div>

          <div style={{flex:1,overflow:'auto',borderRadius:'8px',boxShadow:'0 1px 6px rgba(0,0,0,0.1)'}}>
            <table style={{width:'100%',borderCollapse:'collapse',direction:'rtl',fontSize:'11px',tableLayout:'fixed'}}>
              {/* سرستون‌ها */}
              <thead>
                <tr>
                  <TH idx={0}><span onClick={()=>toggleAll(!allVisibleChecked)} title="انتخاب همه" style={{cursor:'pointer',fontWeight:'700'}}>{allVisibleChecked?'✓':'▢'}</span></TH>
                  <TH idx={1}>سال مالی</TH>
                  <TH idx={2}>سریال</TH>
                  <TH idx={3}>نوع حساب</TH>
                  <TH idx={4}>تاریخ</TH>
                  <TH idx={5}>تاریخ نامه</TH>
                  <TH idx={6} right>منبع اعتبار</TH>
                  <TH idx={7} right>برنامه/طرح</TH>
                  <TH idx={8} right>پروژه</TH>
                  <TH idx={9} right>فصل</TH>
                  <TH idx={10} right>ریزفصل</TH>
                  <TH idx={11} red>دریافتی نقد</TH>
                  <TH idx={12} red>تنخواه دریافتی</TH>
                  <TH idx={13} green>تسویه تنخواه</TH>
                  <TH idx={14} red>اسناد دریافتی</TH>
                  <TH idx={15}>نوع اعتبار</TH>
                  <TH idx={16}>نوع منبع</TH>
                  <TH idx={17}>نوع خزانه</TH>
                  <TH idx={18}>نامه</TH>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r,i)=>{
                  const k = rowKey(r);
                  return (
                  <tr key={k} onClick={()=>toggleOne(k,!checked[k])} style={{background:checked[k]?'#c8e6c9':i%2===0?'#fff':'#fafafa',cursor:'pointer'}}>
                    <td style={{...s.td,fontWeight:'700',color:checked[k]?'#1b5e20':'#bbb'}}>{checked[k]?'✓':''}</td>
                    <td style={s.td}>{toFa(r.financial_year)}</td>
                    <td style={s.td}>{toFa(r.transaction_no)}</td>
                    <td style={s.td}>
                      <span style={{padding:'2px 6px',borderRadius:'4px',fontSize:'9px',fontWeight:'700',
                        background:r.program_kind===0?'#e3f2fd':'#fce4ec',
                        color:r.program_kind===0?'#1565c0':'#c62828'}}>{r.program_type_name}</span>
                    </td>
                    <td style={{...s.td,fontFamily:'monospace'}}>{r.save_date}</td>
                    <td style={{...s.td,fontFamily:'monospace',fontSize:'10px'}}>{r.letter_date||'—'}</td>
                    <td style={{...s.td,textAlign:'right',fontSize:'10px'}} title={r.credit_display}>{r.credit_display||'—'}</td>
                    <td style={{...s.td,textAlign:'right',fontSize:'10px'}} title={r.program_display}>{r.program_display||'—'}</td>
                    <td style={{...s.td,textAlign:'right',fontSize:'10px'}} title={r.project_display}>{r.project_display||'—'}</td>
                    <td style={{...s.td,textAlign:'right',fontSize:'10px'}} title={r.hazineh_display}>{r.hazineh_display||'—'}</td>
                    <td style={{...s.td,textAlign:'right',fontSize:'10px'}} title={r.hazineh_display2}>{r.hazineh_display2||'—'}</td>
                    <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:Math.abs(r.receive_cash_pay||0)>0?'#b71c1c':'#ccc'}}>
                      {numFmt(r.receive_cash_pay)}
                    </td>
                    <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:Math.abs(r.receive_fund_pay||0)>0?'#e65100':'#ccc'}}>
                      {numFmt(r.receive_fund_pay)}
                    </td>
                    <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:Math.abs(r.back_receive_fund_pay||0)>0?'#1b5e20':'#ccc'}}>
                      {numFmt(r.back_receive_fund_pay)}
                    </td>
                    <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:Math.abs(r.receive_document_pay||0)>0?'#4a148c':'#ccc'}}>
                      {numFmt(r.receive_document_pay)}
                    </td>
                    <td style={{...s.td,fontSize:'9px'}}>{r.credit_type_name||'—'}</td>
                    <td style={{...s.td,fontSize:'9px'}}>{r.source_type_name||'—'}</td>
                    <td style={{...s.td,fontSize:'9px'}}>{r.treasury_doc_title||'نامشخص'}</td>
                    <td style={{...s.td,fontSize:'10px'}}>{r.letter_no||'—'}</td>
                  </tr>
                );})}
                {!loading && visibleRows.length===0 && (
                  <tr><td colSpan={19} style={{...s.td,padding:'24px',color:'#999'}}>📭 دریافتی ثبت‌نشده‌ای وجود ندارد</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <LoadMoreButton onClick={loadMore} loading={loadingMore} />

          <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap',flexShrink:0}}>
            <span style={s.badge('#1a2f50')}>{toFa(selectedRows.length)} ردیف</span>
            <span style={s.badge('#b71c1c')}>نقد: {numFmt(totalCash)}</span>
            <span style={s.badge('#e65100')}>تنخواه: {numFmt(totalFund)}</span>
            <span style={s.badge('#1b5e20')}>تسویه: {numFmt(totalBack)}</span>
            <span style={s.badge('#4a148c')}>اسناد: {numFmt(totalDoc)}</span>
            <button onClick={addToDoc} disabled={saving||!selectedRows.length}
              style={{...s.btnP,marginRight:'auto',opacity:(saving||!selectedRows.length)?0.4:1}}>
              {saving?'⏳...':(isNewDoc?'💾 ساخت سند جدید و ثبت':'💾 افزودن به سند')}
            </button>
          </div>
        </div>
        {toast && <div style={{position:'absolute',bottom:'14px',right:'14px',background:'#1b5e20',
          color:'#fff',padding:'10px 20px',borderRadius:'8px',fontSize:'13px',fontWeight:'600',zIndex:10}}>{toast}</div>}
      </div>
    </div>
  );
}

/* استایل‌ها */
const s = {
  overlay:   {position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:3000,
               display:'flex',alignItems:'center',justifyContent:'center',padding:'12px'},
  modal:     {background:'#f0f2f7',borderRadius:'14px',width:'100%',maxWidth:'1300px',
               maxHeight:'90vh',display:'flex',flexDirection:'column',
               boxShadow:'0 24px 64px rgba(0,0,0,0.5)',overflow:'hidden',position:'relative'},
  hdr:       {display:'flex',justifyContent:'space-between',alignItems:'flex-start',
               padding:'14px 18px',background:'linear-gradient(135deg,#0a1628,#4a148c)',flexShrink:0},
  xBtn:      {background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',
               color:'#fff',width:'28px',height:'28px',borderRadius:'50%',cursor:'pointer',
               fontSize:'12px',display:'flex',alignItems:'center',justifyContent:'center'},
  body:      {flex:1,overflowY:'auto',padding:'14px',display:'flex',flexDirection:'column',gap:'10px'},
  err:       {background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'7px',padding:'8px 12px',color:'#c62828',fontSize:'12px'},
  card:      {background:'#fff',borderRadius:'10px',padding:'14px',boxShadow:'0 1px 4px rgba(0,0,0,0.08)'},
  fld:       {display:'flex',flexDirection:'column',gap:'3px'},
  lbl:       {fontSize:'11px',fontWeight:'600',color:'#555'},
  inp:       {padding:'7px 10px',border:'1.5px solid #e0e0e0',borderRadius:'7px',fontSize:'12px',
               fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right',boxSizing:'border-box'},
  td:        {padding:'5px 8px',fontSize:'11px',textAlign:'center',borderBottom:'1px solid #f0f0f0',
               whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'},
  btnP:      {padding:'9px 20px',background:'linear-gradient(135deg,#4a148c,#6a1b9a)',color:'#fff',
               border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnS:      {padding:'8px 14px',background:'#e0e0e0',color:'#333',border:'none',
               borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'12px'},
  badge:     bg => ({padding:'4px 10px',background:bg,color:'#fff',borderRadius:'16px',fontSize:'11px',fontWeight:'700'}),
};
